import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link } from '@tanstack/react-router';
import { isFunction } from 'lodash';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import type { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { itemClassNames, popupClassNames, separatorClassNames } from '~/components2/common';
import Icon from '~/components2/Icon';
import Dialog from './LegacyDialog';

interface ItemBase {
  type: string;
  label: ReactNode;
  isDisabled?: boolean;
  isAllowedTo?: Action;
}

interface SimpleItem extends ItemBase {
  type: 'simple';
  onClick: () => void;
}

interface StaticItem extends ItemBase {
  type: 'static';
}

interface DialogItem extends ItemBase {
  type: 'dialog';
  dialogTitle: string;
  hasCloseButton?: boolean;
  dialogContent: (closeDialog: () => void) => ReactNode;
  onItemSelect?: () => void;
}

interface LinkItem extends ItemBase {
  type: 'link';
  url: string;
}

interface SubMenu extends ItemBase {
  type: 'subMenu';
  label: string;
  items: Item[];
}

interface Separator {
  type: 'separator';
}

export type Item = SubMenu | SimpleItem | StaticItem | DialogItem | LinkItem | Separator;

interface DropdownDialogProps {
  item: DialogItem;
  closeDropdown: () => void;
  setHasOpenDialog: (open: boolean) => void;
  hasCloseButton?: boolean;
  onItemSelect?: () => void;
}

export const DropdownDialog = ({
  item,
  setHasOpenDialog,
  closeDropdown,
  hasCloseButton = true,
  onItemSelect,
}: DropdownDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        setHasOpenDialog(open);
        if (open === false) {
          closeDropdown();
        }
      }}
      title={item.dialogTitle}
      trigger={
        <DropdownMenu.Item
          className={`${itemClassNames} ${item.isDisabled && '!text-grey40'}`}
          onSelect={(e) => {
            e.preventDefault();
            onItemSelect?.();
          }}
          disabled={item.isDisabled}
        >
          {item.label}
        </DropdownMenu.Item>
      }
      hasCloseButton={hasCloseButton}
    >
      {() =>
        item.dialogContent(() => {
          setIsOpen(false);
          closeDropdown();
        })
      }
    </Dialog>
  );
};

interface Props {
  triggerElement: any | ((isOpen: boolean) => any);
  items: Item[];
  align?: any;
  sideOffset?: number;
  alignOffset?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  setIsOpen?: (open: boolean) => void;
  onItemSelect?: () => void;
  container?: HTMLDivElement | null;
  disabled?: boolean;
}

export const Dropdown = ({
  triggerElement,
  items,
  align = 'center',
  sideOffset = 4,
  alignOffset = 0,
  side = 'bottom',
  setIsOpen,
  container,
  disabled = false,
}: Props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasOpenDialog, setHasOpenDialog] = useState(false);

  useEffect(() => {
    setIsOpen?.(dropdownOpen);
  }, [dropdownOpen, setIsOpen]);

  const menuItems = useMemo(() => {
    if (!dropdownOpen) return;
    const renderItems = (items: Item[]) => {
      return items.map((item, i) => {
        if (item.type === 'simple') {
          return (
            <Can
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              action={item.isAllowedTo}
            >
              <DropdownMenu.Item
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={i}
                className={`${itemClassNames} text-sm ${item.isDisabled && '!text-grey40'}`}
                onClick={item.onClick}
                disabled={item.isDisabled}
              >
                {item.label}
              </DropdownMenu.Item>
            </Can>
          );
        } else if (item.type === 'static') {
          return (
            <DropdownMenu.Item
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              className={`${itemClassNames} pointer-events-none text-sm`}
            >
              {item.label}
            </DropdownMenu.Item>
          );
        } else if (item.type === 'dialog') {
          return (
            <Can
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              action={item.isAllowedTo}
            >
              <DropdownDialog
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={i}
                setHasOpenDialog={setHasOpenDialog}
                closeDropdown={() => {
                  setDropdownOpen(false);
                  setHasOpenDialog(false);
                }}
                item={item}
                hasCloseButton={item.hasCloseButton}
                onItemSelect={item.onItemSelect}
              />
            </Can>
          );
        } else if (item.type === 'subMenu') {
          return (
            <DropdownMenu.Sub
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
            >
              <DropdownMenu.SubTrigger
                className={
                  'group relative flex select-none items-center justify-between py-3 pl-4 pr-2 leading-none text-primary outline-none hover:cursor-pointer data-[disabled]:pointer-events-none data-[highlighted]:bg-orange20 data-[highlighted]:data-[state=open]:bg-orange20 data-[state=open]:bg-orange20 data-[highlighted]:data-[state=open]:text-primary-dark data-[highlighted]:text-primary-dark data-[state=open]:text-primary-dark text-sm'
                }
              >
                <span>{item.label}</span>
                <span>
                  <Icon icon="chevronRight" />
                </span>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                  key={i}
                  sideOffset={2}
                  className={popupClassNames}
                  hidden={hasOpenDialog}
                >
                  {renderItems(item.items)}
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          );
        } else if (item.type === 'link') {
          return (
            <Can
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              action={item.isAllowedTo}
            >
              <Link to={item.url}>
                <DropdownMenu.Item className={`${itemClassNames} text-sm ${item.isDisabled && '!text-grey40'}`}>
                  {item.label}
                </DropdownMenu.Item>
              </Link>
            </Can>
          );
        } else if (item.type === 'separator') {
          return (
            <DropdownMenu.Separator
              className={separatorClassNames}
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={`${item}-${i}`}
            />
          );
        } else {
          return null;
        }
      });
    };

    return renderItems(items);
  }, [items, hasOpenDialog, dropdownOpen]);

  return (
    <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenu.Trigger disabled={disabled} onClick={(e) => e.stopPropagation()} asChild>
        {isFunction(triggerElement) ? triggerElement(dropdownOpen) : triggerElement}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal container={container}>
        <DropdownMenu.Content
          className={popupClassNames}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          hidden={hasOpenDialog}
          onCloseAutoFocus={(e) => e.preventDefault()}
          side={side}
        >
          {menuItems}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
